<script setup>
// SidebarTreeNode — render genérico de um nó da sidebar (flat tree).
// Substitui o par SidebarGroupLeaf + SidebarSubGroup. Suporta:
//   - hierarquia visual via padding-left = level * indent
//   - avatar (ícone) ↔ caret (toggle) com swap no hover, padrão ClickUp
//   - leaf (link direto), parent (link + toggle), ou parent só-header
//   - component custom no body (ex: ChannelLeaf)
//   - slot `actions` à direita, escondido até hover
//
// O componente NÃO conhece tree state (expand/visible) — recebe via props.
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import SidebarNodeActions from './SidebarNodeActions.vue';
import { useTruncated } from 'dashboard/composables/useTruncated';

const props = defineProps({
  node: { type: Object, required: true }, // FlatNode do useSidebarTree
  active: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'action']);

// Tooltip com o label completo só quando ele estiver truncado.
const { targetRef, isTruncated, checkTruncation } = useTruncated();

const INDENT_PX = 16;
// Centro horizontal do tile do nivel pai (onde o caret/icon fica). Usado
// pelo pseudo-element ::before pra desenhar a indent guide vertical,
// estilo ClickUp.
const GUIDE_OFFSET_PX = 8;

const indentStyle = computed(() => ({
  paddingLeft: `${props.node.level * INDENT_PX}px`,
  '--guide-left': `${(props.node.level - 1) * INDENT_PX + GUIDE_OFFSET_PX}px`,
}));

const hasCustomComponent = computed(
  () =>
    typeof props.node.component === 'function' || isVNode(props.node.component)
);

const handleToggle = e => {
  e.preventDefault();
  e.stopPropagation();
  emit('toggle');
};

const handleAction = payload => emit('action', { node: props.node, payload });
</script>

<template>
  <li
    class="tree-node-li"
    :data-has-children="node.hasChildren"
    :data-nested="node.level > 0"
  >
    <component
      :is="node.to ? 'router-link' : 'div'"
      :to="node.to"
      class="tree-row group relative flex h-8 items-center gap-2 px-2 py-1 rounded-lg hover:bg-gradient-to-r from-transparent via-n-slate-3/70 to-n-slate-3/70 text-n-slate-11"
      :class="{
        'text-n-blue-text bg-n-alpha-2 active': active,
      }"
      :style="indentStyle"
    >
      <!-- Left: avatar (ícone padrão) e/ou caret toggle -->
      <span
        class="row-left relative inline-flex items-center justify-center shrink-0 w-4 h-4"
      >
        <Icon
          v-if="node.icon && !hasCustomComponent"
          class="row-avatar size-4"
          :icon="node.icon"
        />
        <button
          v-if="node.hasChildren"
          type="button"
          class="row-toggle absolute inset-0 grid place-content-center text-n-slate-11"
          :aria-label="
            isExpanded ? $t('SIDEBAR.COLLAPSE') : $t('SIDEBAR.EXPAND')
          "
          @click="handleToggle"
        >
          <Icon
            icon="i-lucide-chevron-down"
            class="size-3 transition-transform duration-150"
            :class="{ '-rotate-90': !isExpanded }"
          />
        </button>
      </span>

      <!-- Body: custom component ou label nativo -->
      <component
        :is="node.component"
        v-if="hasCustomComponent"
        :label="node.label"
        :icon="node.icon"
        :active
      />
      <div
        v-else
        ref="targetRef"
        v-tooltip="{ content: node.label, disabled: !isTruncated }"
        class="flex-1 truncate min-w-0"
        @mouseenter="checkTruncation"
      >
        {{ node.label }}
      </div>

      <!-- Right: actions (escondido até hover) -->
      <span class="row-actions inline-flex items-center gap-1 ml-auto">
        <slot name="actions" :node="node" :active="active">
          <SidebarNodeActions
            v-if="node.actions && node.actions.length"
            :actions="node.actions"
            @action="handleAction"
          />
        </slot>
      </span>
    </component>
  </li>
</template>

<style scoped>
.tree-node-li {
  list-style: none;
  padding: 2px 0;
}

/* ===========================================================
   ClickUp-style hover swap: avatar (sempre) ↔ caret (no hover)
   Só aplica em rows que têm children (caret faz sentido).
   =========================================================== */

/* default state */
.row-avatar {
  opacity: 1;
}
.row-toggle {
  opacity: 0;
  pointer-events: none;
}
.row-actions {
  opacity: 0;
  pointer-events: none;
}

/* row sem children: só avatar, sem hover swap (caret não existe) */
.tree-node-li[data-has-children='false'] .row-toggle {
  display: none;
}

/* hover swap */
.tree-node-li[data-has-children='true'] .tree-row:hover .row-avatar {
  opacity: 0;
}
.tree-node-li[data-has-children='true'] .tree-row:hover .row-toggle {
  opacity: 1;
  pointer-events: auto;
}

/* actions: hover em qualquer row (independe de children) */
.tree-row:hover .row-actions {
  opacity: 1;
  pointer-events: auto;
}

/* keyboard focus dentro do toggle: força visível pra acessibilidade */
.row-toggle:focus-visible {
  opacity: 1;
  pointer-events: auto;
}

/* ===========================================================
   Indent guide vertical (padrao ClickUp): linha fina ligando
   o ancestral expandido aos seus children. So aparece em nodes
   com level > 0; posicao calculada via --guide-left no :style.
   =========================================================== */
.tree-node-li[data-nested='true'] .tree-row::before {
  content: '';
  position: absolute;
  left: var(--guide-left);
  top: -2px;
  bottom: -2px;
  width: 1px;
  background-color: rgb(var(--slate-5));
  pointer-events: none;
}
</style>
